//@ts-check

import React, { useContext, useEffect, useRef, useState } from 'react';
import { find, pickBy } from 'lodash-es';
import { TUserSelection, IProduct, IVariant } from '../../types/PDP';
import { pushEventToGTMDataLayer } from '../../../../utils/gtm-data-layer';
import './style.scss';
import { getLocationInfoSync } from '../../../../utils/location';
import Checkbox from '../../../_shared/ui-components/YogaUI/Checkbox';
import { getSoldOutCopy, isComingSoon } from '../../../../utils/plp-pdp-shared';
import { ProductContext } from '../../ProductContext';
import { scrollToSimilarItems } from '../../utils';
import { ALO_API_HOST } from 'src/scripts/utils/constants';
import { ShippingCountryState } from 'src/scripts/utils/shipping';

const NotifyMe = (props: {
  customerEmail: string;
  variant?: IVariant | undefined;
  product: IProduct;
  userSelection: TUserSelection;
  itemIsProbablyAnAccessory: boolean;
  uniqueIDforCheckbox?: string; // Needed if component present on the page more than once
  shouldShowNotifyMe?: boolean;
  tags?: string[];
}) => {
  const {
    customerEmail,
    product,
    userSelection,
    itemIsProbablyAnAccessory,
    uniqueIDforCheckbox = 'single',
    shouldShowNotifyMe = false,
    variant,
    tags = [],
  } = props;

  const BIS_SUBSCRIBE_ENDPOINT = `${ALO_API_HOST}/v1/back-in-stock/subscribe`;
  const [email, setEmail] = useState(customerEmail);
  const [errorMessage, setErrorMessage] = useState('');
  const { similarItemsCarouselExists } = useContext(ProductContext);
  const [doesAcceptMarketing, setDoesAcceptMarketing] = useState(() => {
    const locationInfo = getLocationInfoSync();
    return !locationInfo?.is_eu;
  });
  const [showForm, setShowForm] = useState(true);
  const [showCustomMessage, setShowCustomMessage] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const timeoutIds = useRef<number[]>([]);
  const productDataForColor =
    product.productDataByColorName && product.productDataByColorName[userSelection.Color || ''];
  const { baseTitle: productTitle, id: productId } = productDataForColor || {};
  const selectedVariant = variant ? variant : find(product.variants, { selectionOptions: userSelection });
  const { acceptsEmailMarketing, phone } = window.getCustomerData?.() || {};

  useEffect(() => {
    setErrorMessage('');
    setShowForm(true);
    setShowCustomMessage(false);
    timeoutIds.current.forEach((id) => window.clearTimeout(id));
    timeoutIds.current = [];

    return () => {
      timeoutIds.current.forEach((id) => window.clearTimeout(id));
      timeoutIds.current = [];
    };
  }, [selectedVariant, productId]);

  if (!selectedVariant || !shouldShowNotifyMe) return null;

  /*
    * [REVISIT]
    so the autoFocus did not work beyond the first instance
    of the input... additionally I would have like to have
    it be focused on the click of an item OUTSIDE/Parent of
    this component but it didnt seem to work... additionally
    we would have still needed a delay to fire the focus...

    here it is self contained but in my opinion not ideal...
  */

  const soldOutCopy = getSoldOutCopy(tags);
  const productComingSoon = isComingSoon(tags);
  const subText = `Be the first to know when this ${itemIsProbablyAnAccessory ? `item` : `size`} is${
    !productComingSoon ? ` back` : ''
  } in stock!`;

  const bisMessageExpiryTimeout = 8000;
  const notifyMeSuccessHandler = ({ variantId }: { variantId: string }) => {
    setShowForm(false);
    setShowCustomMessage(true);
    // Hide custom message and show form again after timeout
    timeoutIds.current.push(
      window.setTimeout(() => {
        setShowForm(true);
        setShowCustomMessage(false);
      }, bisMessageExpiryTimeout)
    );

    pushEventToGTMDataLayer({
      event: 'alo_notifyMe',
      notifyMeEventInformation: {
        success: true,
        notifyMeProductInfo: {
          variantId: variantId,
        },
      },
    });
  };

  return (
    <div>
      <form
        id="iStock-wrapper"
        data-id={selectedVariant.id}
        style={{ display: 'block' }}
        className="new-version outside"
        onSubmit={async (e) => {
          e.preventDefault();
          // LND-2595 custom BIS implementation
          const { id: variantId } = selectedVariant;
          try {
            const response = await fetch(BIS_SUBSCRIBE_ENDPOINT, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(
                pickBy(
                  {
                    product_variant_id: variantId,
                    product_id: productId,
                    product_sku: selectedVariant.sku,
                    user_email: email,
                    user_country_code: ShippingCountryState.countryCode,
                    user_phone: (phone || '').replace(/\D/g, ''),
                    user_agent: navigator.userAgent,
                    user_accepts_marketing: acceptsEmailMarketing,
                    user_utc_offset: new Date().getTimezoneOffset() * 60,
                  },
                  (v: unknown) => v !== undefined && v !== null && v !== ''
                )
              ),
            });
            const responseJson = await response.json();
            if (response.status === 200) {
              setCustomMessage(
                responseJson.message || 'Thanks! We will notify you when this product becomes available!'
              );
              notifyMeSuccessHandler({ variantId });
            } else {
              const error = notifyMeErrorHandler({ variantId }, responseJson);
              setErrorMessage(error);
              timeoutIds.current.push(window.setTimeout(() => setErrorMessage(''), bisMessageExpiryTimeout));
            }
          } catch (err) {
            setErrorMessage('An unexpected error occurred.');
          }
        }}
      >
        <div className="notify-sold-out">
          <p className={`out-of-stock-p notify-text ${productComingSoon ? '' : 'sold-out-text'}`}>
            {productComingSoon ? 'Coming Soon' : soldOutCopy}
          </p>
          {similarItemsCarouselExists && (
            <button
              type="button"
              aria-label="Go to similar items"
              className="similar-items"
              onClick={scrollToSimilarItems}
            >
              See Similar Items
            </button>
          )}
        </div>
        {showForm && (
          <div className="BISform" data-id={selectedVariant.id}>
            <p className="notify-text">{subText}</p>
            <div id="iStock-fields" className="new-version">
              <input
                type="email"
                id="iStock-email-id"
                className="form-control new-version email-input"
                data-id={selectedVariant.id}
                name="iStock-email-id"
                placeholder="Enter your email"
                value={email}
                required
                pattern="^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$"
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
                    e.stopPropagation();
                  }
                }}
              />
              <input type="hidden" id="iStock-product" name="iStock-product" value={productTitle} />
              <input type="submit" id="iStock-notify-btn" className="btn btn-primary d-block new-version" value=" " />
            </div>
            <Checkbox
              options={{
                id: `notifyMe-${uniqueIDforCheckbox}`,
                label: 'Sign up for Alo news & drop alerts.',
                defaultChecked: doesAcceptMarketing,
                checked: doesAcceptMarketing,
                onChange: (event) => {
                  setDoesAcceptMarketing(event.target.checked);
                },
                wrapperClasses: 'notify-me-checkbox-wrapper',
              }}
            />
          </div>
        )}
        {showCustomMessage && <div className="small-p black text-left custom-message">{customMessage}</div>}
        <div id="iStock-error-msg-custom" className={`small-p black text-left ${!errorMessage ? 'd-none' : ''}`}>
          {errorMessage}
        </div>
      </form>
    </div>
  );
};

export default NotifyMe;

export interface BISErrorResponse {
  detail: {
    message: string;
  };
}

const notifyMeErrorHandler = ({ variantId }: { variantId: string }, caughtResponseObject: BISErrorResponse) => {
  pushEventToGTMDataLayer({
    event: 'alo_notifyMe',
    notifyMeEventInformation: {
      success: false,
      notifyMeProductInfo: {
        variantId: variantId,
      },
    },
  });

  return caughtResponseObject.detail.message;
};
